import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * PersonalFinanceAnimation - 个人理财基础动画
 * 
 * 展示收入、支出、储蓄、投资的分配
 * 可视化 50/30/20 法则
 * 
 * 适用场景：
 * - 财商教育
 * - 理财入门
 * - 个人财务管理教程
 * 
 * 教育价值评分：9/10
 * - 直观展示理财法则
 * - 帮助建立财务规划意识
 */

// 50/30/20 法则数据
const budgetCategories = [
  {
    name: '必需支出',
    nameEn: 'Needs',
    percentage: 50,
    color: '#4A90E2',
    icon: '🏠',
    items: ['房租/房贷', '水电费', '交通', '基本食物'],
  },
  {
    name: '个人需求',
    nameEn: 'Wants',
    percentage: 30,
    color: '#F59E0B',
    icon: '🎮',
    items: ['娱乐', '购物', '外出就餐', '订阅服务'],
  },
  {
    name: '储蓄投资',
    nameEn: 'Savings',
    percentage: 20,
    color: '#10B981',
    icon: '💰',
    items: ['应急基金', '退休储蓄', '投资理财', '还债'],
  },
];

// 收入支出数据
const monthlyData = {
  income: 10000,
  categories: [
    { name: '必需支出', amount: 5000, color: '#4A90E2' },
    { name: '个人需求', amount: 3000, color: '#F59E0B' },
    { name: '储蓄投资', amount: 2000, color: '#10B981' },
  ],
};

// 饼图扇形组件
const PieSlice: React.FC<{
  percentage: number;
  startAngle: number;
  color: string;
  progress: number;
  centerX: number;
  centerY: number;
  radius: number;
}> = ({ percentage, startAngle, color, progress, centerX, centerY, radius }) => {
  const angle = percentage * 3.6 * progress;
  const endAngle = startAngle + angle;

  const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
  const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
  const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
  const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

  const largeArcFlag = angle > 180 ? 1 : 0;

  const pathD = `
    M ${centerX} ${centerY}
    L ${x1} ${y1}
    A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}
    Z
  `;

  return (
    <path
      d={pathD}
      fill={color}
      stroke="#0a0e27"
      strokeWidth={3}
      style={{
        filter: `drop-shadow(0 4px 12px ${color}40)`,
      }}
    />
  );
};

// 动态数值组件
const AnimatedNumber: React.FC<{
  value: number;
  progress: number;
  prefix?: string;
  suffix?: string;
  color?: string;
}> = ({ value, progress, prefix = '', suffix = '', color = '#fff' }) => {
  const displayValue = Math.floor(value * progress);
  
  return (
    <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
};

// 柱状图组件
const BudgetBar: React.FC<{
  category: typeof budgetCategories[0];
  index: number;
  frame: number;
  fps: number;
  maxValue: number;
}> = ({ category, index, frame, fps, maxValue }) => {
  const delay = 60 + index * 15;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 80 },
  });

  const barHeight = (category.percentage / maxValue) * 280 * progress;

  if (frame < delay) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
      }}
    >
      {/* 数值 */}
      <div
        style={{
          fontSize: 36,
          fontWeight: 700,
          color: category.color,
          opacity: progress,
          textShadow: `0 0 20px ${category.color}60`,
        }}
      >
        <AnimatedNumber value={category.percentage} progress={progress} suffix="%" />
      </div>

      {/* 柱子 */}
      <div
        style={{
          width: 140,
          height: barHeight,
          background: `linear-gradient(180deg, ${category.color} 0%, ${category.color}CC 100%)`,
          borderRadius: '12px 12px 0 0',
          boxShadow: `0 0 30px ${category.color}40, inset 0 2px 0 rgba(255,255,255,0.2)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* 柱子光泽 */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
          }}
        />
      </div>

      {/* 图标和标签 */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          opacity: progress,
        }}
      >
        <span style={{ fontSize: 48 }}>{category.icon}</span>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 600, color: '#fff' }}>
            {category.name}
          </div>
          <div style={{ fontSize: 20, color: category.color }}>
            {category.nameEn}
          </div>
        </div>
      </div>
    </div>
  );
};

// 分类卡片组件
const CategoryCard: React.FC<{
  category: typeof budgetCategories[0];
  index: number;
  frame: number;
  fps: number;
}> = ({ category, index, frame, fps }) => {
  const delay = 150 + index * 20;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  if (frame < delay) return null;

  return (
    <div
      style={{
        position: 'absolute',
        right: 30 + index * 380,
        top: 790,
        width: 340,
        padding: 24,
        background: 'rgba(255, 255, 255, 0.06)',
        backdropFilter: 'blur(12px)',
        borderRadius: 16,
        border: `2px solid ${category.color}30`,
        opacity: progress,
        transform: `translateY(${(1 - progress) * 20}px)`,
        boxShadow: `0 8px 32px ${category.color}15`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 40 }}>{category.icon}</span>
        <div>
          <div style={{ fontSize: 26, fontWeight: 600, color: '#fff' }}>
            {category.name}
          </div>
          <div style={{ fontSize: 24, color: category.color }}>
            {category.percentage}%
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {category.items.map((item, i) => (
          <div
            key={i}
            style={{
              fontSize: 22,
              color: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ color: category.color }}>•</span>
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};

export const PersonalFinanceAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 饼图动画进度
  const pieProgress = interpolate(frame, [20, 80], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // 标题动画
  const titleProgress = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const titleY = interpolate(titleProgress, [0, 1], [-60, 0]);

  // 计算饼图起始角度
  let currentAngle = -90;

  // 收入数值动画
  const incomeProgress = interpolate(frame, [30, 90], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* 装饰性背景 */}
      <div
        style={{
          position: 'absolute',
          top: -300,
          right: -300,
          width: 800,
          height: 800,
          background: 'radial-gradient(circle, rgba(74, 144, 226, 0.08), transparent)',
          borderRadius: '50%',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -300,
          left: -300,
          width: 800,
          height: 800,
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.08), transparent)',
          borderRadius: '50%',
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 80,
          opacity: titleProgress,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 72,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #4A90E2 0%, #10B981 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          50/30/20 理财法则
        </h1>
        <p
          style={{
            fontSize: 30,
            margin: '16px 0 0 0',
            color: 'rgba(255, 255, 255, 0.6)',
          }}
        >
          Personal Finance Basics · 轻松管理你的收入
        </p>
      </div>

      {/* 月收入展示 */}
      <div
        style={{
          position: 'absolute',
          top: 100,
          right: 80,
          padding: '20px 40px',
          background: 'rgba(74, 144, 226, 0.1)',
          border: '1px solid rgba(74, 144, 226, 0.3)',
          borderRadius: 16,
          opacity: incomeProgress,
        }}
      >
        <div style={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.6)', marginBottom: 8 }}>
          月收入 Monthly Income
        </div>
        <div style={{ fontSize: 48, fontWeight: 700, color: '#4A90E2' }}>
          ¥<AnimatedNumber value={monthlyData.income} progress={incomeProgress} />
        </div>
      </div>

      {/* 饼图 */}
      <div
        style={{
          position: 'absolute',
          left: 200,
          top: 300,
        }}
      >
        <svg width={440} height={440}>
          {budgetCategories.map((category, index) => {
            const startAngle = currentAngle;
            currentAngle += category.percentage * 3.6 * pieProgress;
            
            return (
              <PieSlice
                key={index}
                percentage={category.percentage}
                startAngle={startAngle}
                color={category.color}
                progress={pieProgress}
                centerX={220}
                centerY={220}
                radius={200}
              />
            );
          })}
          
          {/* 中心圆 */}
          <circle cx={220} cy={220} r={80} fill="#0a0e27" />
          <text
            x={220}
            y={204}
            textAnchor="middle"
            fill="#fff"
            fontSize={28}
            fontWeight={600}
          >
            收入
          </text>
          <text
            x={220}
            y={250}
            textAnchor="middle"
            fill="#4A90E2"
            fontSize={32}
            fontWeight={700}
          >
            100%
          </text>
        </svg>

        {/* 饼图标签 */}
        {budgetCategories.map((category, index) => {
          const midAngle = -90 + (index * 120 + 60) * Math.PI / 180;
          const labelX = 220 + 240 * Math.cos(midAngle);
          const labelY = 220 + 240 * Math.sin(midAngle);
          
          const labelProgress = spring({
            frame: frame - 80 - index * 10,
            fps,
            config: { damping: 12 },
          });

          if (frame < 80 + index * 10) return null;

          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                left: labelX - 44,
                top: labelY - 20,
                opacity: labelProgress,
                transform: `scale(${labelProgress})`,
              }}
            >
              <div
                style={{
                  padding: '8px 16px',
                  background: `${category.color}20`,
                  border: `1px solid ${category.color}40`,
                  borderRadius: 8,
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: 22, fontWeight: 600, color: category.color }}>
                  {category.percentage}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* 柱状图 */}
      <div
        style={{
          position: 'absolute',
          right: 140,
          top: 280,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 60,
          height: 420,
        }}
      >
        {budgetCategories.map((category, index) => (
          <BudgetBar
            key={index}
            category={category}
            index={index}
            frame={frame}
            fps={fps}
            maxValue={100}
          />
        ))}
      </div>

      {/* 公式提示 */}
      <div
        style={{
          position: 'absolute',
          top: 790,
          left: 130,
          opacity: interpolate(frame, [100, 130], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        <div
          style={{
            padding: '24px 40px',
            background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.1), rgba(16, 185, 129, 0.1))',
            border: '1px solid rgba(74, 144, 226, 0.3)',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <span style={{ fontSize: 36, color: '#4A90E2', fontWeight: 700 }}>50%</span>
          <span style={{ fontSize: 28, color: 'rgba(255, 255, 255, 0.5)' }}>+</span>
          <span style={{ fontSize: 36, color: '#F59E0B', fontWeight: 700 }}>30%</span>
          <span style={{ fontSize: 28, color: 'rgba(255, 255, 255, 0.5)' }}>+</span>
          <span style={{ fontSize: 36, color: '#10B981', fontWeight: 700 }}>20%</span>
          <span style={{ fontSize: 28, color: 'rgba(255, 255, 255, 0.5)' }}>=</span>
          <span style={{ fontSize: 36, color: '#fff', fontWeight: 700 }}>100%</span>
        </div>
      </div>

      {/* 分类卡片 */}
      {budgetCategories.map((category, index) => (
        <CategoryCard
          key={index}
          category={category}
          index={index}
          frame={frame}
          fps={fps}
        />
      ))}

      {/* 底部提示 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 130,
          opacity: interpolate(frame, [180, 210], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px 24px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 12,
          }}
        >
          <span style={{ fontSize: 30 }}>💡</span>
          <span style={{ fontSize: 24, color: '#10B981' }}>
            从今天开始规划你的财务
          </span>
        </div>
      </div>
    </AbsoluteFill>
  );
};
